import { FONT_FAMILIES } from './fonts';

export const FONT_SIZES = {
  xs: 10,
  meta: 11, // metadata label/value lines (Plaud grammar)
  sm: 12,
  base: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 28,
  '4xl': 32,
  display: 52,
};

/**
 * The canonical type system. Components style text by spreading a preset
 * (`...TYPOGRAPHY.h1`) and overriding only color/margins — never by
 * assembling FONT_FAMILIES + FONT_SIZES by hand (ESLint enforces this
 * outside src/theme/).
 *
 * Scale rationale:
 * - Headings carry negative tracking that tightens as size grows; this is
 *   what separates a designed heading from a default one at Inter's sizes.
 * - `display` and `statNumeral` use tabular numerals so readiness
 *   percentages and counts don't shift width as they tick.
 * - `overline` is the single sanctioned section-label style (uppercase,
 *   wide tracking) — the 'WILDFIRE READINESS' pattern, systematized.
 * - `labelSmall` is the compact-tag register (tighter 0.5 tracking) for
 *   metadata micro-labels; don't use it for section headers.
 * - `screenTitle` is the Plaud register: large LIGHT weight for screen
 *   titles and structure addresses. It replaces bold `h1`/`h2` at the top
 *   of screens; headings inside content keep the h-scale.
 * - `metadata` is the small gray label/value line (Location / Type /
 *   Status blocks, feed meta rows). Pair with textTertiary/textSecondary.
 */
export const TYPOGRAPHY = {
  // Screen titles + structure addresses (Plaud light register)
  screenTitle: {
    fontFamily: FONT_FAMILIES.light,
    fontSize: FONT_SIZES['3xl'],
    lineHeight: 34,
    letterSpacing: -0.28, // -0.01em at 28px
  },

  // Small gray metadata label/value lines
  metadata: {
    fontFamily: FONT_FAMILIES.regular,
    fontSize: FONT_SIZES.meta,
    lineHeight: 18,
  },

  // Hero numerals (readiness percentage)
  display: {
    fontFamily: FONT_FAMILIES.bold,
    fontSize: FONT_SIZES.display,
    lineHeight: 54,
    letterSpacing: -1.5,
    fontVariant: ['tabular-nums' as const],
  },

  // Headings
  h1: {
    fontFamily: FONT_FAMILIES.bold,
    fontSize: FONT_SIZES['4xl'],
    lineHeight: 38,
    letterSpacing: -0.5,
  },
  h2: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES['3xl'],
    lineHeight: 34,
    letterSpacing: -0.3,
  },
  h3: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES['2xl'],
    lineHeight: 30,
    letterSpacing: -0.2,
  },
  h4: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES.lg,
    lineHeight: 24,
    letterSpacing: -0.1,
  },

  // Body
  body: {
    fontFamily: FONT_FAMILIES.regular,
    fontSize: FONT_SIZES.base,
    lineHeight: 22,
  },
  bodySmall: {
    fontFamily: FONT_FAMILIES.regular,
    fontSize: FONT_SIZES.sm,
    lineHeight: 18,
  },
  // A 16px medium label with real breathing room (list rows) — distinct
  // from `label`, which is 12px.
  bodyLarge: {
    fontFamily: FONT_FAMILIES.medium,
    fontSize: FONT_SIZES.md,
    lineHeight: 22,
  },

  // UI
  label: {
    fontFamily: FONT_FAMILIES.medium,
    fontSize: FONT_SIZES.sm,
    lineHeight: 16,
  },
  labelSmall: {
    fontFamily: FONT_FAMILIES.medium,
    fontSize: FONT_SIZES.xs,
    lineHeight: 14,
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
  },
  overline: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES.xs,
    lineHeight: 14,
    letterSpacing: 1.2,
    textTransform: 'uppercase' as const,
  },
  button: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES.base,
    lineHeight: 20,
  },
  caption: {
    fontFamily: FONT_FAMILIES.regular,
    fontSize: FONT_SIZES.xs,
    lineHeight: 14,
  },

  // Readiness meter percent — number set large and LIGHT (Plaud grammar)
  numeralLight: {
    fontFamily: FONT_FAMILIES.light,
    fontSize: FONT_SIZES['2xl'],
    lineHeight: 28,
    letterSpacing: -0.24, // -0.01em at 24px
    fontVariant: ['tabular-nums' as const],
  },

  // Full-screen presentation register (MapSlideshow, shared over a Zoom
  // call) — the structure address under a photo and a title card's headline
  // both need to read from across a room, not a phone or a card.
  presentationTitle: {
    fontFamily: FONT_FAMILIES.bold,
    fontSize: FONT_SIZES.display,
    lineHeight: 56,
    letterSpacing: -1,
  },
  // One step under presentationTitle: the address line under a slideshow
  // photo. Big enough for a room, small enough that a long street name
  // stays on one line next to the category pill.
  presentationSubtitle: {
    fontFamily: FONT_FAMILIES.bold,
    fontSize: FONT_SIZES['4xl'],
    lineHeight: 40,
    letterSpacing: -0.5,
  },

  // Stats (counts, percentages in cards and table cells)
  statNumeral: {
    fontFamily: FONT_FAMILIES.bold,
    fontSize: FONT_SIZES['2xl'],
    lineHeight: 28,
    letterSpacing: -0.5,
    fontVariant: ['tabular-nums' as const],
  },

  // Button's sm/xs label register (Button.tsx composes this from `button`
  // + a size override; named here so other compact labels don't hand-roll it)
  buttonSmall: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES.sm,
    lineHeight: 20,
  },
  // Selected SegmentedControl option / active feed filter chip
  chip: {
    fontFamily: FONT_FAMILIES.semibold,
    fontSize: FONT_SIZES.sm,
    lineHeight: 16,
  },
  // A News post's own page: reading size, regular weight, room between lines
  reading: {
    fontFamily: FONT_FAMILIES.regular,
    fontSize: FONT_SIZES.md,
    lineHeight: 26,
  },
};

export type TypographyPreset = keyof typeof TYPOGRAPHY;

/**
 * Picks a weight for a preset without importing FONT_FAMILIES directly
 * (ESLint blocks that import outside src/theme/). expo-font declares every
 * Inter family at the same default weight, so `fontWeight` on Inter text
 * does nothing below 600 and fakes bold at 600+ — the family, not a
 * `fontWeight` property, is what actually changes the render (Sept 29 2026).
 */
export function withWeight<T extends object>(
  preset: T,
  weight: keyof typeof FONT_FAMILIES
): T & { fontFamily: string } {
  return { ...preset, fontFamily: FONT_FAMILIES[weight] };
}
